import {ambassadorWithHeaders} from '../../utils/ambassador.utils';
import {ControllerFlowAPI} from '@wix/yoshi-flow-editor';
import {SiteStore} from '@wix/wixstores-client-storefront-sdk';
import {HttpError} from '@wix/http-client';
import {getOrderPaymentRequest} from '@wix/ambassador-ecom-v1-order-payment-request/http';
import {PaymentRequestModel} from '../../models/paymentRequest/PaymentRequest.model';
import {OrderPaymentRequest} from '@wix/ambassador-ecom-v1-order-payment-request/types';
import {FedopsInteractions} from '../../../components/PaymentRequest/constants';
import {PaymentRequestErrorCode} from '../../../types/paymentRequestApp.types';
import {getOrderPaymentRequestApplicationErrors} from '../../../types/checkoutApp.types';
import {SPECS} from '../../../common/constants';
import {ViewerContext} from '@wix/ecom-platform-storefront-common';
import {IWidgetControllerConfig} from '@wix/native-components-infra';

export class PaymentRequestService {
  private readonly flowAPI: ControllerFlowAPI;
  private readonly siteStore: SiteStore;
  private readonly viewerContext: ViewerContext;
  private readonly controllerConfig: IWidgetControllerConfig;
  public readonly orderPaymentRequestId?: string;
  public paymentRequest!: PaymentRequestModel;
  public paymentRequestError?: PaymentRequestErrorCode;
  public paymentRequestResolvedError?: string;

  constructor({
    siteStore,
    flowAPI,
    viewerContext,
    controllerConfig,
  }: {
    flowAPI: ControllerFlowAPI;
    siteStore: SiteStore;
    viewerContext: ViewerContext;
    controllerConfig: IWidgetControllerConfig;
  }) {
    this.siteStore = siteStore;
    this.flowAPI = flowAPI;
    this.viewerContext = viewerContext;
    this.controllerConfig = controllerConfig;
    const moveCheckoutToViewerContext = this.viewerContext.isExperimentEnabled(SPECS.MoveCheckoutToViewerContext);
    this.orderPaymentRequestId = moveCheckoutToViewerContext
      ? this.controllerConfig.wixCodeApi.location.path[1]
      : this.siteStore.location.path[1];
  }

  public async init(): Promise<void> {
    this.flowAPI.panoramaClient?.transaction(FedopsInteractions.FetchPaymentRequest).start();
    this.flowAPI.fedops.interactionStarted(FedopsInteractions.FetchPaymentRequest);
    await this.fetchPaymentRequest();
    this.flowAPI.panoramaClient?.transaction(FedopsInteractions.FetchPaymentRequest).finish();
    this.flowAPI.fedops.interactionEnded(FedopsInteractions.FetchPaymentRequest);
  }

  public setPaymentRequestError(errorCode: PaymentRequestErrorCode) {
    this.paymentRequestError = errorCode;
  }

  public async fetchPaymentRequest(): Promise<void> {
    if (!this.orderPaymentRequestId) {
      console.error('No orderPaymentRequestId');
      this.setPaymentRequestError(PaymentRequestErrorCode.notFound);
    } else {
      try {
        const {data} = await ambassadorWithHeaders(
          getOrderPaymentRequest({
            orderPaymentRequestId: this.orderPaymentRequestId,
          }),
          this.siteStore,
          this.viewerContext,
          this.flowAPI,
          {} as getOrderPaymentRequestApplicationErrors
        );

        /* istanbul ignore next */
        if (data.orderPaymentRequest) {
          this.setPaymentRequest(data.orderPaymentRequest);
        } else {
          /* istanbul ignore next */
          console.error('No order payment request data from the API');
          /* istanbul ignore next */
          this.setPaymentRequestError(PaymentRequestErrorCode.notFound);
        }
      } catch (error) {
        const httpError = error as HttpError;
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        const errorDataDetails = httpError?.response?.data?.details;
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        const ErrorCode = errorDataDetails?.applicationError?.code as string;
        const code =
          ErrorCode === PaymentRequestErrorCode.notFound
            ? PaymentRequestErrorCode.notFound
            : PaymentRequestErrorCode.generalError;

        if (this.flowAPI.essentials.experiments.enabled(SPECS.PayLinkShowGetResolvedError)) {
          this.paymentRequestResolvedError = this.flowAPI.errorHandler.getResolvedError(error).message;
        }

        this.setPaymentRequestError(code);
      }
    }
  }

  private setPaymentRequest(paymentRequest: OrderPaymentRequest) {
    this.paymentRequest = new PaymentRequestModel(paymentRequest, {
      locale: this.flowAPI.translations.config.language,
    });
  }
}
